/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useNavigate } from 'react-router';
import { useEffect, useRef } from 'react';
import {
  ArrowRight,
  GraduationCap,
  Clock,
  MessageSquareText,
  ShieldCheck,
  Lightbulb,
  BookOpen,
  Plus,
  X,
  Globe,
} from 'lucide-react';
import { useRudderstack } from '@/clients/shared-hooks/use-rudderstack';
import styles from './landing-styles.module.css';

export function LandingPageV2() {
  const navigate = useNavigate();

  // Refs for the sections to observe to create a parallax effect
  // assign these to the corresponding sections in the JSX
  const introRef = useRef<HTMLDivElement | null>(null);
  const worksRef = useRef<HTMLDivElement | null>(null);
  const powerRef = useRef<HTMLDivElement | null>(null);
  const bestRef = useRef<HTMLDivElement | null>(null);
  const trainingRef = useRef<HTMLDivElement | null>(null);
  const llmsRef = useRef<HTMLDivElement | null>(null);
  const badgesRef = useRef<HTMLDivElement | null>(null);
  const loveRef = useRef<HTMLDivElement | null>(null);
  const teamsRef = useRef<HTMLDivElement | null>(null);
  const faqRef = useRef<HTMLDivElement | null>(null);

  // Track page view
  const analytics = useRudderstack();
  useEffect(() => {
    if (analytics) {
      analytics?.page(
        'Landing',
        {
          domain: 'prof.ai',
        },
        {
          page: {
            domain: 'prof.ai',
          },
        },
      );
    }
  }, [analytics]);

  const trackCta = (cta: string) => {
    analytics?.track('Landing CTA', {
      app: 'ProfAI',
      cta,
    });
  };

  // turn on the parallax effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles['animate-slide-up']);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      },
    );
    if (introRef.current) observer.observe(introRef.current);
    if (worksRef.current) observer.observe(worksRef.current);
    if (powerRef.current) observer.observe(powerRef.current);
    if (bestRef.current) observer.observe(bestRef.current);
    if (trainingRef.current) observer.observe(trainingRef.current);
    if (llmsRef.current) observer.observe(llmsRef.current);
    if (badgesRef.current) observer.observe(badgesRef.current);
    if (loveRef.current) observer.observe(loveRef.current);
    if (teamsRef.current) observer.observe(teamsRef.current);
    if (faqRef.current) observer.observe(faqRef.current);

    // turn on the parallax effect
    return () => observer.disconnect();
  }, []);
  return (
    <div className={styles['landing-v2-page']}>
      <div className={styles['landing-v2-content-section']}>
        <div className={styles['landing-v2-hero-right']}>
          <video className={styles['landing-v2-hero-placeholder-video']} loop autoPlay muted>
            <source
              src="https://media.sectionai.com/marketing/PAI-landingPage-August21-v5.mp4"
              type="video/mp4"
            />
          </video>
        </div>
        <div className={styles['landing-v2-hero-left']}>
          <div className={styles['landing-v2-hero-title']}>
            The <span style={{ color: 'var(--colors-tomato)' }}>fast,</span>{' '}
            <span style={{ color: 'var(--colors-pumpkin)' }}>fun,</span>{' '}
            <span style={{ color: 'var(--colors-cornflower)' }}>free</span> way to get AI-certified
          </div>
          <div className={styles['landing-v2-hero-btn-group']}>
            <button
              className={styles['landing-v2-default-btn']}
              onClick={() => {
                trackCta('Get started');
                navigate('/sign-up');
              }}
            >
              Get started
            </button>
            <button
              className={styles['landing-v2-secondary-btn']}
              onClick={() => {
                trackCta('I already have an account');
                navigate('/login');
              }}
            >
              I already have an account
            </button>
            <button
              className={styles['landing-v2-corp-btn']}
              onClick={() => {
                trackCta('Did your company give you access to ProfAI?');
                navigate('/section');
              }}
            >
              Did your company give you access to ProfAI?{' '}
              <span className={styles['landing-v2-corp-btn-end']}>Click here</span>
            </button>
          </div>
        </div>
      </div>
      <div
        className={`${styles['landing-v2-content-section']} ${styles['landing-v2-value']} ${styles['parallax-section']}`}
        ref={introRef}
      >
        <img
          className={`${styles['landing-v2-value-img']} ${styles['landing-v2-mobile-invisible']}`}
          alt={'ProfAI Avatar'}
          src="/img/landingPage/valueV2.png"
        />
        <div className={styles['landing-v2-value-text']}>
          <div className={styles['landing-v2-value-title']}>
            Make AI your professional superpower in minutes
          </div>
          <div className={styles['landing-v2-value-description']}>
            AI is a once-in-a-generation technological shift… and mastering it is a prerequisite for
            getting ahead. But figuring out what to use ChatGPT or Copilot for isn’t easy.
            <br />
            <br />
            ProfAI is the lightning-fast, hyper-personalized way to master prompting and find great
            AI use cases, so you can start doing more exciting work right away.
            <br />
            <br />
            <span className={styles['landing-v2-value-quote-block']}>
              <i>
                <b>
                  &quot;If you&apos;re looking to build you (or your team&apos;s) AI proficiency, I
                  wholeheartedly recommend this AI coach.&quot;
                </b>
              </i>
              <br />- Naomi Davis, Senior Brand and Marketing Strategist, Santander
            </span>
          </div>
        </div>
      </div>
      <div className={`${styles['landing-v2-works']} ${styles['parallax-section']}`} ref={worksRef}>
        <div className={styles['landing-v2-content-section']}>
          <div className={styles['landing-v2-section-title-group']}>
            <div className={styles['landing-v2-section-title']}>How ProfAI works</div>
            <div className={styles['landing-v2-section-title-sub']}>
              Personalized AI training designed to fit your job and your time.
            </div>
          </div>
          <div className={styles['landing-v2-section-numbers-group']}>
            <div className={styles['landing-v2-number']}>01</div>
            <div className={styles['landing-v2-number-text']}>
              You tell ProfAI about your role and priorities
            </div>
            <div className={styles['landing-v2-number-text']}>
              ProfAI tailors exercises to your unique work
            </div>
            <div className={styles['landing-v2-number']}>02</div>
            <div className={styles['landing-v2-number']}>03</div>
            <div className={styles['landing-v2-number-text']}>
              You master core AI skills and leave with a certification
            </div>
          </div>
          <img
            className={`${styles['landing-v2-works-img']} ${styles['landing-v2-mobile-invisible']}`}
            alt={'How ProfAI works'}
            src="/img/landingPage/powerCenter.svg"
          />
          <div style={{ float: 'left', width: '100%' }}>
            <button
              className={`${styles['landing-v2-default-btn']} ${styles['landing-v2-mobile-default-btn']}`}
              style={{ float: 'none', display: 'inline-block', marginBottom: '30px' }}
              onClick={() => {
                trackCta('Get started');
                navigate('/sign-up');
              }}
            >
              Start your ProfAI journey now
              <ArrowRight
                size={24}
                color="white"
                strokeWidth={1.5}
                className={styles['landing-v2-alt-btn-arrow']}
              />
            </button>
          </div>
        </div>
      </div>
      <div
        className={`${styles['landing-v2-power-learning']} ${styles['parallax-section']}`}
        ref={powerRef}
      >
        <div className={styles['landing-v2-content-section']}>
          <div className={styles['landing-v2-section-title-group']}>
            <div className={styles['landing-v2-section-title']}>
              Master the core skills of every AI expert
            </div>
          </div>
          <div className={`${styles['landing-v2-power-tile']} ${styles['landing-tile-1']}`}>
            <div className={styles['landing-v2-power-tile-main']}>
              <div className={styles['landing-v2-power-tile-img']}>
                <MessageSquareText size={40} color="var(--colors-cornflower)" strokeWidth={1.5} />
              </div>
            </div>
            <div className={styles['landing-v2-power-tile-title']}>Write great prompts</div>
            <div className={styles['landing-v2-power-tile-description']}>
              ProfAI will teach you to write the best prompt for every situation, using real
              examples from your work.
            </div>
          </div>
          <div className={`${styles['landing-v2-power-tile']} ${styles['landing-v2-tile-2']}`}>
            <div className={styles['landing-v2-power-tile-main']}>
              <div className={styles['landing-v2-power-tile-img']}>
                <ShieldCheck size={40} color="var(--colors-cornflower)" strokeWidth={1.5} />
              </div>
            </div>
            <div className={styles['landing-v2-power-tile-title']}>Use AI responsibly</div>
            <div className={styles['landing-v2-power-tile-description']}>
              Protect your data when using AI, and learn to spot hallucinations when they appear in
              AI&apos;s outputs
            </div>
          </div>
          <div className={`${styles['landing-v2-power-tile']} ${styles['landing-v2-tile-3']}`}>
            <div className={styles['landing-v2-power-tile-main']}>
              <div className={styles['landing-v2-power-tile-img']}>
                <Lightbulb size={40} color="var(--colors-cornflower)" strokeWidth={1.5} />
              </div>
            </div>
            <div className={styles['landing-v2-power-tile-title']}>Find a great AI use case</div>
            <div className={styles['landing-v2-power-tile-description']}>
              Discover use cases for your actual work and start putting them to use right away
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${styles['landing-v2-content-section']} ${styles['landing-v2-best-uses']} ${styles['parallax-section']}`}
        ref={bestRef}
      >
        <div className={styles['landing-v2-section-title-group']}>
          <div className={styles['landing-v2-section-title']}>
            &quot;This is one of the
            <span> best uses</span> of 30
            <br className={styles['landing-v2-mobile-invisible']} /> minutes I can think of.&quot;
          </div>
          <div className={styles['landing-v2-section-title-sub']}>
            <b>Andrew Johnston</b>
            <span className={styles['landing-v2-best-uses-dot']}>&#x2022;</span>
            <span className={styles['landing-v2-best-uses-company']}>
              Employer Partnerships, Handshake
            </span>
          </div>
          <button
            className={`${styles['landing-v2-default-btn']} ${styles['landing-v2-mobile-default-btn']}`}
            style={{ float: 'none', display: 'inline-block' }}
            onClick={() => {
              trackCta('Get started');
              navigate('/sign-up');
            }}
          >
            Join Andrew. Get started now
            <ArrowRight
              size={24}
              color="white"
              strokeWidth={1.5}
              className={styles['landing-v2-alt-btn-arrow']}
            />
          </button>
        </div>
      </div>

      <div
        className={`${styles['landing-v2-ai-training']} ${styles['parallax-section']}`}
        ref={trainingRef}
      >
        <div className={styles['landing-v2-content-section']}>
          <div className={styles['landing-v2-section-title-group']}>
            <div className={styles['landing-v2-section-title']}>
              AI training personalized to you
            </div>
          </div>
          <div
            className={`${styles['landing-v2-works-tile']} ${styles['landing-v2-works-tile-1']}`}
          >
            <div className={styles['landing-v2-works-tile-main']}>
              <div className={styles['landing-v2-works-tile-img']}>
                <GraduationCap size={40} color="var(--colors-cornflower)" strokeWidth={1.5} />
              </div>
            </div>
            <div className={styles['landing-v2-works-tile-title']}>
              AI skills specifically for your job
            </div>
            <div className={styles['landing-v2-works-tile-description']}>
              Every lesson is tailored to you — no generic content.
            </div>
          </div>
          <div
            className={`${styles['landing-v2-works-tile']} ${styles['landing-v2-works-tile-2']}`}
          >
            <div className={styles['landing-v2-works-tile-main']}>
              <div className={styles['landing-v2-works-tile-img']}>
                <BookOpen size={40} color="var(--colors-warm-75)" strokeWidth={1.5} />
              </div>
            </div>
            <div className={styles['landing-v2-works-tile-title']}>
              Hands-on practice that&apos;s never boring
            </div>
            <div className={styles['landing-v2-works-tile-description']}>
              10+ short, interactive exercises designed for real-world tasks.
            </div>
          </div>
          <div
            className={`${styles['landing-v2-works-tile']} ${styles['landing-v2-works-tile-3']}`}
          >
            <div className={styles['landing-v2-works-tile-main']}>
              <div className={styles['landing-v2-works-tile-img']}>
                <Clock size={40} color="black" strokeWidth={1.5} />
              </div>
            </div>
            <div className={styles['landing-v2-works-tile-title']}>Real-time coaching</div>
            <div className={styles['landing-v2-works-tile-description']}>
              ProfAI gives you feedback on your AI skills and tailors your program as you learn.
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${styles['landing-v2-across-llms']} ${styles['parallax-section']}`}
        ref={llmsRef}
      >
        <div className={styles['landing-v2-content-section']}>
          <div className={styles['landing-v2-section-title-group']}>
            <div className={styles['landing-v2-section-title']}>
              Applicable across LLMs and AI tools
            </div>
            <div className={styles['landing-v2-section-title-sub']}>
              Don&apos;t limit yourself to one AI model (most AI experts don&apos;t). ProfAI teaches
              concepts that can be applied across LLMs and tools, including:
            </div>
          </div>
          <div className={styles['landing-v2-section-llms-group']}>
            <img
              className={styles['landing-v2-llm-img']}
              alt={'ChatGPT'}
              src="/img/landingPage/chatgpt_logo.svg"
            />
            <img
              className={styles['landing-v2-llm-img']}
              alt={'Claude'}
              src="/img/landingPage/claude_logo.svg"
            />
            <img
              className={styles['landing-v2-llm-img']}
              alt={'Perplexity'}
              src="/img/landingPage/perplexity_logo.svg"
            />
            <img
              className={styles['landing-v2-llm-img']}
              alt={'Copilot'}
              src="/img/landingPage/copilot_logo.svg"
            />
            <img
              className={styles['landing-v2-llm-img']}
              alt={'DeepSeek'}
              src="/img/landingPage/deepseek_logo.svg"
            />
            <img
              className={styles['landing-v2-llm-img']}
              alt={'Grok'}
              src="/img/landingPage/grok_logo.svg"
            />
            <img
              className={styles['landing-v2-llm-img']}
              alt={'Cohere'}
              src="/img/landingPage/cohere_logo.svg"
            />
          </div>
        </div>
      </div>
      <div
        className={`${styles['landing-v2-badges']} ${styles['parallax-section']}`}
        ref={badgesRef}
      >
        <div className={styles['landing-v2-content-section-sm']}>
          <img
            className={styles['landing-v2-badges-img']}
            alt={'ProfAI Badges'}
            src="/img/landingPage/basicAIProficiencyBadge.svg"
          />
          <div className={styles['landing-v2-badges-text']}>
            <div className={styles['landing-v2-badges-title']}>
              Earn an AI certification in <br />
              30 minutes
            </div>
            <div className={styles['landing-v2-badges-description']}>
              Most users walk away with a LinkedIn badge to share with their network or employer in
              30 minutes.
            </div>
            <button
              className={`${styles['landing-v2-default-btn']} ${styles['landing-v2-mobile-default-btn']}`}
              style={{ float: 'none', display: 'inline-block' }}
              onClick={() => {
                trackCta('Get started');
                navigate('/sign-up');
              }}
            >
              Get started on your certification
              <ArrowRight
                size={24}
                color="white"
                strokeWidth={1.5}
                className={styles['landing-v2-alt-btn-arrow']}
              />
            </button>
          </div>
        </div>
      </div>
      <div className={`${styles['landing-v2-love']} ${styles['parallax-section']}`} ref={loveRef}>
        <div className={styles['landing-v2-content-section']}>
          <div className={styles['landing-v2-section-title-group']}>
            <div className={styles['landing-v2-section-title']}>AI power users love ProfAI</div>
            <div className={styles['landing-v2-section-title-sub']}>
              See for yourself what thousands of ProfAI users say about the experience.
            </div>
          </div>
        </div>
        <div 
          className={`${styles['landing-v2-love-test-scroll-holder']} ${styles['landing-v2-love-scroll-invisible']}`}
        >
          <div className={styles['landing-v2-section-love-group-scroll']}>
            <div className={styles['landing-v2-love-test-scroll']}>
              <div className={styles['landing-v2-love-test-top']}>
                <img
                  className={styles['landing-v2-love-img-profile']}
                  alt={'Profile'}
                  src="/img/landingPage/test1Profile.jpeg"
                />
                <div className={styles['landing-v2-love-test-text']}>
                  <span className={styles['landing-v2-love-test-name']}>
                    Andrew Johnston
                  </span>
                  <span className={styles['landing-v2-love-test-title']}>
                    Employer Partnerships @ Handshake | Co-Host of ...
                  </span>
                  <span className={styles['landing-v2-love-test-time']}>
                    16h   &#x2022; 
                    <Globe
                      size={14}
                      strokeWidth={1}
                      className={styles['landing-v2-love-test-time-globe']}
                    />
                  </span>
                </div>
                <div className={styles['landing-v2-love-test-message']}>
                  I'm excited to share that I'm now certified in ProfAI: AI Proficiency from <b>Section</b>!
                  <br/>
                  <br/>
                  One of the best uses of 30 minutes I can think of and ProfAI brings great awareness to the risks/pitfalls that you might run into when leveraging your GenAI platform of choice.
                </div>
              </div>
            </div>
            <div className={styles['landing-v2-love-test-scroll']}>
              <div className={styles['landing-v2-love-test-top']}>
                <img
                  className={styles['landing-v2-love-img-profile']}
                  alt={'Profile'}
                  src="/img/landingPage/test2Profile.jpeg"
                />
                <div className={styles['landing-v2-love-test-text']}>
                  <span className={styles['landing-v2-love-test-name']}>
                    Michelle L.
                  </span>
                  <span className={styles['landing-v2-love-test-title']}>
                    Project Management Supervisor at Coegi
                  </span>
                  <span className={styles['landing-v2-love-test-time']}>
                    1h   &#x2022; 
                    <Globe
                      size={14}
                      strokeWidth={1}
                      className={styles['landing-v2-love-test-time-globe']}
                    />
                  </span>
                </div>
                <div className={styles['landing-v2-love-test-message']}>
                  I'm now certified in ProfAl: Al Proficiency from <b>Section</b>!
                  <br/>
                  <br/>
                  I really enjoyed this customizable training, it made all the examples so much easier to relate to and determine how to best use Al prompting in my work day.
                </div>
              </div>
            </div>
            <div className={styles['landing-v2-love-test-scroll']}>
              <div className={styles['landing-v2-love-test-top']}>
                <img
                  className={styles['landing-v2-love-img-profile']}
                  alt={'Profile'}
                  src="/img/landingPage/test3Profile.jpeg"
                />
                <div className={styles['landing-v2-love-test-text']}>
                  <span className={styles['landing-v2-love-test-name']}>
                    Morgan Clearkin
                  </span>
                  <span className={styles['landing-v2-love-test-title']}>
                    Al Specialist at KPMG New Zealand
                  </span>
                  <span className={styles['landing-v2-love-test-time']}>
                    13h   &#x2022; 
                    <Globe
                      size={14}
                      strokeWidth={1}
                      className={styles['landing-v2-love-test-time-globe']}
                    />
                  </span>
                </div>
                <div className={styles['landing-v2-love-test-message']}>
                  Want a super easy and quick intro to Al that is tailored to your industry?
                  <br/>
                  <br/>
                  <b>Section</b> recently made their ProfAl tool freely available and it is actually an awesome primer for how to build prompts, use Al responsibly and find use cases for Al.
                  <br/>
                  <br/>
                  And you can get through the key parts in around 30-mins too!
                  <br/>
                  <br/>
                  Check it out here: <a href="https://www.prof.ai" target="_blank">https://www.prof.ai</a>
                </div>
              </div>
            </div>
            <div className={styles['landing-v2-love-test-scroll']}>
              <div className={styles['landing-v2-love-test-top']}>
                <img
                  className={styles['landing-v2-love-img-profile']}
                  alt={'Profile'}
                  src="/img/landingPage/test4Profile.jpeg"
                />
                <div className={styles['landing-v2-love-test-text']}>
                  <span className={styles['landing-v2-love-test-name']}>
                    Travis Tallent
                  </span>
                  <span className={styles['landing-v2-love-test-title']}>
                    Managing Director, SEO & CRO | brightonSEO ...
                  </span>
                  <span className={styles['landing-v2-love-test-time']}>
                    2d   &#x2022; 
                    <Globe
                      size={14}
                      strokeWidth={1}
                      className={styles['landing-v2-love-test-time-globe']}
                    />
                  </span>
                </div>
                <div className={styles['landing-v2-love-test-message']}>
                  I'm excited to share that I'm now certified in ProfAl: Al Proficiency from <b>Section</b>!
                  <br/>
                  <br/>
                  This was a fantastic, personalized course that covers the basics of prompt engineering, Al responsibility, and how to apply it in your specific role. I can't wait to learn more!
                </div>
              </div>
            </div>
            <div className={styles['landing-v2-love-test-scroll']}>
              <div className={styles['landing-v2-love-test-top']}>
                <img
                  className={styles['landing-v2-love-img-profile']}
                  alt={'Profile'}
                  src="/img/landingPage/test5Profile.jpeg"
                />
                <div className={styles['landing-v2-love-test-text']}>
                  <span className={styles['landing-v2-love-test-name']}>
                    Noah Woodruff
                  </span>
                  <span className={styles['landing-v2-love-test-title']}>
                    Sourcing & Procurement Professional at International Paper
                  </span>
                  <span className={styles['landing-v2-love-test-time']}>
                    2d   &#x2022;   Edited   &#x2022; 
                    <Globe
                      size={14}
                      strokeWidth={1}
                      className={styles['landing-v2-love-test-time-globe']}
                    />
                  </span>
                </div>
                <div className={styles['landing-v2-love-test-message']}>
                  I'm excited to share that I'm now certified in ProfAl: Al Proficiency from <b>Section</b>!
                  <br/>
                  <br/>
                  This program highlights how Al can provide training customized to your exact needs as a person. Highly recommend this training to anyone curious about Al.
                </div>
              </div>
            </div>

            <div className={styles['landing-v2-love-test-scroll']}>
              <div className={styles['landing-v2-love-test-top']}>
                <img
                  className={styles['landing-v2-love-img-profile']}
                  alt={'Profile'}
                  src="/img/landingPage/test6Profile.jpeg"
                />
                <div className={styles['landing-v2-love-test-text']}>
                  <span className={styles['landing-v2-love-test-name']}>
                    Emre Güney
                  </span>
                  <span className={styles['landing-v2-love-test-title']}>
                    Head of Marketing @ Skyscanner
                  </span>
                  <span className={styles['landing-v2-love-test-time']}>
                    2d   &#x2022;
                    <Globe
                      size={14}
                      strokeWidth={1}
                      className={styles['landing-v2-love-test-time-globe']}
                    />
                  </span>
                </div>
                <div className={styles['landing-v2-love-test-message']}>
                  Just wrapped up the ProfAl: Al Proficiency course from <b>Section</b> and certified.
                  <br/>
                  <br/>
                  Clear, hands-on, and immediately applicable.
                </div>
              </div>
            </div>

            <div className={styles['landing-v2-love-test-scroll']}>
              <div className={styles['landing-v2-love-test-top']}>
                <img
                  className={styles['landing-v2-love-img-profile']}
                  alt={'Profile'}
                  src="/img/landingPage/test7Profile.jpeg"
                />
                <div className={styles['landing-v2-love-test-text']}>
                  <span className={styles['landing-v2-love-test-name']}>
                    Anne-Marie Sheehan
                  </span>
                  <span className={styles['landing-v2-love-test-title']}>
                    FP&A Manager Europe
                  </span>
                  <span className={styles['landing-v2-love-test-time']}>
                    9h   &#x2022;
                    <Globe
                      size={14}
                      strokeWidth={1}
                      className={styles['landing-v2-love-test-time-globe']}
                    />
                  </span>
                </div>
                <div className={styles['landing-v2-love-test-message']}>
                  Happy to share that I'm now certified in ProfAl: Al Proficiency from <b>Section</b>! This course course was short, sweet, and straight to the point. This was my first time doing a course which used Al to create a personalised learning experience for the user. Really engaging and effective.
                </div>
              </div>
            </div>

            <div className={styles['landing-v2-love-test-scroll']}>
              <div className={styles['landing-v2-love-test-top']}>
                <img
                  className={styles['landing-v2-love-img-profile']}
                  alt={'Profile'}
                  src="/img/landingPage/test8Profile.jpeg"
                />
                <div className={styles['landing-v2-love-test-text']}>
                  <span className={styles['landing-v2-love-test-name']}>
                    Florencia Stonski
                  </span>
                  <span className={styles['landing-v2-love-test-title']}>
                    Senior Regional Consumer & Shopper Planning Manager
                  </span>
                  <span className={styles['landing-v2-love-test-time']}>
                    39m   &#x2022;
                    <Globe
                      size={14}
                      strokeWidth={1}
                      className={styles['landing-v2-love-test-time-globe']}
                    />
                  </span>
                </div>
                <div className={styles['landing-v2-love-test-message']}>
                  My favourite e-learning platfoms Section did it again!
                  <br/>
                  <br/>
                  This time, enabling a FREE, high quality, personalized & engaging hashtag <b>#Al</b> training.
                  <br/>
                  <br/>
                  Highly recommend it to anyone wanting to practice some prompting and learn applications of Al to your current role.
                  <br/>
                  <br/>
                  <a href="https://Inkd.in/e3tgY9J4" target="_blank">https://Inkd.in/e3tgY9J4</a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div 
          className={`${styles['landing-v2-content-section']} ${styles['landing-v2-love-scroll-visible']}`}
        >
          <div className={styles['landing-v2-section-love-group']}>
            <img
              className={styles['landing-v2-love-img']}
              alt={'Testimonial'}
              src="/img/landingPage/test1.png"
            />
            <img
              className={styles['landing-v2-love-img']}
              alt={'Testimonial'}
              src="/img/landingPage/test2.png"
            />
            <img
              className={styles['landing-v2-love-img']}
              alt={'Testimonial'}
              src="/img/landingPage/test3.png"
            />
          </div>
        </div>
      </div>
      <div className={`${styles['landing-v2-teams']} ${styles['parallax-section']}`} ref={teamsRef}>
        <div className={styles['landing-v2-content-section-sm']}>
          <div
            className={`${styles['landing-v2-section-title-group']} ${styles['landing-v2-mobile-text-center']}`}
          >
            <div
              className={`${styles['landing-v2-section-title']} ${styles['landing-v2-title-alt']}`}
            >
              Want ProfAI access for your whole team?
            </div>
            <div
              className={`${styles['landing-v2-section-title-sub']} ${styles['landing-v2-title-alt']}`}
            >
              We can customize ProfAI to your organization&apos;s policies and priorities.
            </div>
            <a
              className={`${styles['landing-v2-default-btn']} ${styles['landing-v2-mobile-default-btn']}`}
              href="https://www.sectionai.com/ai/profai#live-demo"
              target="_blank"
              rel="noreferrer"
              style={{ float: 'none', display: 'inline-block' }}
              onClick={() => {
                trackCta('Book a demo');
              }}
            >
              Book a meeting
              <ArrowRight
                size={24}
                color="white"
                strokeWidth={1.5}
                className={styles['landing-v2-alt-btn-arrow']}
              />
            </a>
          </div>
        </div>
      </div>
      <div className={`${styles['landing-v2-faq']} ${styles['parallax-section']}`} ref={faqRef}>
        <div className={styles['landing-v2-content-section']}>
          <div className={styles['landing-v2-section-title-group']}>
            <div className={styles['landing-v2-section-title']}>Frequently asked questions</div>
          </div>
          <div className={styles['landing-v2-section-faq-group']}>
            <div
              className={styles['landing-v2-faq-line']}
              onClick={(e) => {
                (e.currentTarget as Element).classList.toggle(styles['line-active']);
              }}
            >
              <div className={styles['landing-v2-faq-number']}>01</div>
              <div className={styles['landing-v2-faq-title']}>What is ProfAI?</div>
              <div className={styles['landing-v2-faq-btn']}>
                <Plus
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-open']}
                />
                <X
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-close']}
                />
              </div>
              <div className={styles['landing-v2-faq-answer']}>
                ProfAI is a highly personalized coach that teaches AI skills like prompting, finding
                a great use case, and using AI responsibly.
              </div>
            </div>
            <div
              className={styles['landing-v2-faq-line']}
              onClick={(e) => {
                (e.currentTarget as Element).classList.toggle(styles['line-active']);
              }}
            >
              <div className={styles['landing-v2-faq-number']}>02</div>
              <div className={styles['landing-v2-faq-title']}>How do I access ProfAI?</div>
              <div className={styles['landing-v2-faq-btn']}>
                <Plus
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-open']}
                />
                <X
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-close']}
                />
              </div>
              <div className={styles['landing-v2-faq-answer']}>
                It&apos;s easy. Just click on “get started” above and create an account. From there,
                you&apos;ll tell ProfAI a little about yourself and jump into learning right away.
              </div>
            </div>
            <div
              className={styles['landing-v2-faq-line']}
              onClick={(e) => {
                (e.currentTarget as Element).classList.toggle(styles['line-active']);
              }}
            >
              <div className={styles['landing-v2-faq-number']}>03</div>
              <div className={styles['landing-v2-faq-title']}>
                What does ProfAI do with my data?
              </div>
              <div className={styles['landing-v2-faq-btn']}>
                <Plus
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-open']}
                />
                <X
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-close']}
                />
              </div>
              <div className={styles['landing-v2-faq-answer']}>
                ProfAI retains your data for 30 days to help us improve the product. We do not train
                the model on your answers and never share your information with third parties.
              </div>
            </div>
            <div
              className={styles['landing-v2-faq-line']}
              onClick={(e) => {
                (e.currentTarget as Element).classList.toggle(styles['line-active']);
              }}
            >
              <div className={styles['landing-v2-faq-number']}>04</div>
              <div className={styles['landing-v2-faq-title']}>Is ProfAI free?</div>
              <div className={styles['landing-v2-faq-btn']}>
                <Plus
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-open']}
                />
                <X
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-close']}
                />
              </div>
              <div className={styles['landing-v2-faq-answer']}>
                ProfAI is free for individuals to access. If you want advanced features like team
                customization or analytics and reporting,{' '}
                <a
                  href="https://www.sectionai.com/ai/profai#live-demo"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    float: 'none',
                    display: 'inline-block',
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                  }}
                  onClick={() => {
                    trackCta('Book a demo faq 1');
                  }}
                >
                  book a meeting
                </a>{' '}
                with our team to discuss.
              </div>
            </div>
            <div
              className={styles['landing-v2-faq-line']}
              onClick={(e) => {
                (e.currentTarget as Element).classList.toggle(styles['line-active']);
              }}
            >
              <div className={styles['landing-v2-faq-number']}>05</div>
              <div className={styles['landing-v2-faq-title']}>
                Can I get ProfAI for my team / organization?
              </div>
              <div className={styles['landing-v2-faq-btn']}>
                <Plus
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-open']}
                />
                <X
                  size={18}
                  color="#ccc"
                  strokeWidth={1.5}
                  className={styles['landing-v2-faq-btn-close']}
                />
              </div>
              <div className={styles['landing-v2-faq-answer']}>
                Yes - we partner with organizations over 500 people to deploy ProfAI to their teams.{' '}
                <a
                  href="https://www.sectionai.com/ai/profai#live-demo"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    float: 'none',
                    display: 'inline-block',
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                  }}
                  onClick={() => {
                    trackCta('Book a demo faq 2');
                  }}
                >
                  Book a meeting
                </a>{' '}
                to learn more.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
